import { CompareResult } from './compare-result';
import { endOfDay, format, isAfter, isBefore, startOfDay } from 'date-fns';
import { assert } from './assert-condition';

type comparableDateType = Date | string | number;

export function compareDatetime(filterDatetime: comparableDateType, cellValue: comparableDateType): CompareResult {
    const filterDateStartDay = startOfDay(new Date(filterDatetime));
    const filterDateEndDay = endOfDay(new Date(filterDatetime));
    const cellValueDate = new Date(cellValue);
    if (!cellValue || isBefore(cellValueDate, filterDateStartDay)) {
        return -1;
    }
    if (isAfter(cellValueDate, filterDateEndDay)) {
        return 1;
    }

    return 0;
}

export function isOlderThan30Days(date: Date | string): boolean {
    const monthAgo = new Date().setDate(new Date().getDate() - 30);
    return date !== null && compareDatetime(date, monthAgo) === 1;
}

/**
 * GET FORMATTED DATE
 *
 * @param date
 * @Param @Optional - formatStyle, specify specific format style
 *
 * any input which is valid for JS Date object will be valid here
 * Note: strings like "12-**" are valid in some browsers.
 *
 * @return
 * date as string in the request format or the default following format: 'dd-MMM-yyyy HH:mm' OR undefined for invalid value
 */
export function getFormattedDate(date: Date | string | number, requestedFormat?: string): string | undefined {
    const parsedDate = new Date(date);

    if (isNaN(parsedDate.getTime())) {
        return undefined;
    }

    if (requestedFormat) {
        return format(parsedDate, requestedFormat);
    }

    return format(parsedDate, 'dd-MMM-yyyy');
}

/**
 * GET FORMATTED DATE INCLUDE TIME
 *
 * @param date
 * any input which is valid for JS Date object will be valid here
 * Note: strings like "12-**" are valid in some browsers.
 *
 * @return
 * date as string in the following format: 'dd-MMM-yyyy HH:mm' OR undefined for invalid value
 */
export function getFormattedDateIncludeTime(date: Date | string | number): string | undefined {
    const parsedDate = new Date(date);

    if (isNaN(parsedDate.getTime())) {
        return undefined;
    }

    return format(parsedDate, 'dd-MMM-yyyy HH:mm');
}

/**
 * GET FORMATTED DATE INCLUDE SECONDS
 *
 * @param date
 * any input which is valid for JS Date object will be valid here
 * Note: strings like "12-**" are valid in some browsers.
 *
 * @return
 * date as string in the following format: 'dd-MMM-yyyy HH:mm:ss' OR undefined for invalid value
 */
export function getFormattedDateIncludeSeconds(date: Date | string | number): string | undefined {
    const parsedDate = new Date(date);

    if (isNaN(parsedDate.getTime())) {
        return undefined;
    }

    return format(parsedDate, 'dd-MMM-yyyy HH:mm:ss');
}

export function getFormattedTimeDiff(time1: number, time2: number): string {
    const timeDifference = Math.abs(time1 - time2);

    let diffHours = Math.floor(timeDifference / (60 * 60 * 1000));
    const diffMinutes = Math.floor((timeDifference / (60 * 1000)) % 60);

    const diffMinutesString = diffMinutes ? `${diffMinutes} minutes ` : '';

    if (diffHours) {
        if (diffHours > 24) {
            const diffDays = Math.floor(diffHours / 24);
            const diffDaysString = diffDays > 1 ? `${diffDays} days` : `1 day`;
            diffHours = diffHours % 24;
            return `${diffDaysString} ${diffHours} hours`;
        }
        return `${diffHours} hours ` + diffMinutesString;
    }

    const diffSeconds = Math.floor((timeDifference / 1000) % 60);
    return diffMinutesString + `${diffSeconds} seconds`;
}

export function getFormattedShortWeekDays(dayKeysSplitByComma: string): string {
    const dayKeys = dayKeysSplitByComma.split(',').map((key) => +key);

    ensureNumberArrayIsWeekDays(dayKeys);

    const weekdayValues = Object.values(WeekdayEnum);

    return dayKeys
        .sort((a, b) => weekdayValues.indexOf(+a) - weekdayValues.indexOf(+b))
        .map((day: number) => getWeekDayShortName(+day))
        .join(', ');
}

function ensureNumberArrayIsWeekDays(dayStrKeys: number[]): asserts dayStrKeys is WeekdayEnum[] {
    const weekdayValues = Object.values(WeekdayEnum);

    if (
        dayStrKeys.some((dayKey) => {
            return !weekdayValues.includes(+dayKey);
        })
    ) {
        throw new Error('Invalid Value: dayStrKeys is not WeekdayEnum[]');
    }
}

export enum WeekdayEnum {
    Sunday = 1,
    Monday,
    Tuesday,
    Wednesday,
    Thursday,
    Friday,
    Saturday
}

export function getWeekDayShortName(s: WeekdayEnum): string {
    return WeekdayEnum[s].toString().substring(0, 3);
}
